import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, Paper,
  Alert, CircularProgress, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Chip, Snackbar, Tooltip,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Slider, Divider,
  ToggleButtonGroup, ToggleButton, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import { QrOptions, DEFAULT_QR_OPTIONS, QrListItem } from './types';
import ImageIcon from '@mui/icons-material/Image';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  getQrPreviewSvg, generateQr, downloadQrTiff, downloadQrPng, listQrCodes, deleteQrCode,
  DynamicQrItem, listDynamicQr, createDynamicQr, updateDynamicQr, deleteDynamicQr,
  QrLogo, uploadQrLogo, listQrLogos, deleteQrLogo,
  LogoSearchResult, searchAssetsForLogo,
} from './qrApi';
import { API_BASE_URL } from '../../config';

interface SavedPreset {
  id: string;
  name: string;
  options: QrOptions;
  locked: boolean;
}

const PRESETS_KEY = 'qrcode-presets';
function loadPresets(): SavedPreset[] { try { return JSON.parse(localStorage.getItem(PRESETS_KEY) || '[]'); } catch { return []; } }
function persistPresets(p: SavedPreset[]) { localStorage.setItem(PRESETS_KEY, JSON.stringify(p)); }

const EC_LABELS: Record<string, string> = {
  L: 'L – 7% återställning', M: 'M – 15% återställning',
  Q: 'Q – 25% återställning', H: 'H – 30% återställning',
};

interface QrGeneratorPageProps {
  categoryIds?: string[];
}

const QrGeneratorPage: React.FC<QrGeneratorPageProps> = ({ categoryIds }) => {
  const [qrMode, setQrMode] = useState<'static' | 'dynamic'>('static');

  // Static state
  const [contentInput, setContentInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [options, setOptions] = useState<QrOptions>(DEFAULT_QR_OPTIONS);
  const [svgPreview, setSvgPreview] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generatedAssetId, setGeneratedAssetId] = useState<string | null>(null);
  const [history, setHistory] = useState<QrListItem[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Presets
  const [presets, setPresets] = useState<SavedPreset[]>(loadPresets);
  const [activeTab, setActiveTab] = useState('standard');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Dynamic state
  const [dynName, setDynName] = useState('');
  const [dynUrl, setDynUrl] = useState('');
  const [dynBaseUrl, setDynBaseUrl] = useState(window.location.origin);
  const [dynGenerating, setDynGenerating] = useState(false);
  const [dynamicList, setDynamicList] = useState<DynamicQrItem[]>([]);
  const [editingQr, setEditingQr] = useState<DynamicQrItem | null>(null);
  const [editUrl, setEditUrl] = useState('');

  // Logo state
  const [logos, setLogos] = useState<QrLogo[]>([]);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoSearch, setLogoSearch] = useState('');
  const [logoSearchResults, setLogoSearchResults] = useState<LogoSearchResult[]>([]);
  const [logoSearching, setLogoSearching] = useState(false);
  const [selectedAssetLogo, setSelectedAssetLogo] = useState<LogoSearchResult | null>(null);

  const previewTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadHistory = useCallback(async () => { try { setHistory(await listQrCodes()); } catch {} }, []);
  const loadDynamic = useCallback(async () => { try { setDynamicList(await listDynamicQr()); } catch {} }, []);
  const loadLogos = useCallback(async () => { try { setLogos(await listQrLogos()); } catch {} }, []);

  useEffect(() => { loadHistory(); loadDynamic(); loadLogos(); }, [loadHistory, loadDynamic, loadLogos]);

  const updatePreview = useCallback(async (content: string, opts: QrOptions) => {
    if (!content.trim()) { setSvgPreview(null); setPreviewError(null); return; }
    setPreviewLoading(true); setPreviewError(null);
    try { setSvgPreview(await getQrPreviewSvg(content.trim(), opts)); }
    catch (err: any) { setPreviewError(err.response?.data?.error || err.message); setSvgPreview(null); }
    finally { setPreviewLoading(false); }
  }, []);

  useEffect(() => {
    if (qrMode !== 'static') return;
    if (previewTimeout.current) clearTimeout(previewTimeout.current);
    previewTimeout.current = setTimeout(() => updatePreview(contentInput, options), 400);
    return () => { if (previewTimeout.current) clearTimeout(previewTimeout.current); };
  }, [contentInput, options, updatePreview, qrMode]);

  const handleTabChange = (_: any, newTab: string) => {
    setActiveTab(newTab);
    if (newTab === 'standard') setOptions(DEFAULT_QR_OPTIONS);
    else { const p = presets.find(x => x.id === newTab); if (p) setOptions({ ...DEFAULT_QR_OPTIONS, ...p.options }); }
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    const np: SavedPreset = { id: `qp_${Date.now()}`, name: presetName.trim(), options: { ...options }, locked: false };
    const updated = [...presets, np]; setPresets(updated); persistPresets(updated);
    setActiveTab(np.id); setSaveDialogOpen(false); setPresetName('');
    setSnackbar({ open: true, message: `Inställning "${np.name}" sparad`, severity: 'success' });
  };

  const handleToggleLock = (id: string) => { const u = presets.map(p => p.id === id ? { ...p, locked: !p.locked } : p); setPresets(u); persistPresets(u); };
  const handleDeletePreset = (id: string) => {
    if (presets.find(p => p.id === id)?.locked) return;
    const u = presets.filter(p => p.id !== id); setPresets(u); persistPresets(u);
    if (activeTab === id) { setActiveTab('standard'); setOptions(DEFAULT_QR_OPTIONS); }
  };

  const handleGenerate = async () => {
    if (!contentInput.trim() || !nameInput.trim()) { setSnackbar({ open: true, message: 'Ange namn och innehåll', severity: 'error' }); return; }
    setGenerating(true); setGeneratedAssetId(null);
    try {
      const result = await generateQr(contentInput.trim(), nameInput.trim(), options, categoryIds);
      setGeneratedAssetId(result.asset._id);
      setSnackbar({ open: true, message: result.replaced ? `QR-kod "${nameInput}" uppdaterad` : `QR-kod "${nameInput}" skapad`, severity: 'success' });
      loadHistory();
    } catch (err: any) { setSnackbar({ open: true, message: err.response?.data?.error || 'Fel', severity: 'error' }); }
    finally { setGenerating(false); }
  };

  const handleDynamicGenerate = async () => {
    if (!dynName.trim() || !dynUrl.trim()) { setSnackbar({ open: true, message: 'Ange namn och destinations-URL', severity: 'error' }); return; }
    setDynGenerating(true);
    try {
      const result = await createDynamicQr(dynName.trim(), dynUrl.trim(), options, dynBaseUrl.trim(), categoryIds);
      setSnackbar({ open: true, message: `Dynamisk QR "${dynName}" skapad – redirect-URL: ${result.redirect.redirectUrl}`, severity: 'success' });
      setDynName(''); setDynUrl('');
      loadDynamic(); loadHistory();
    } catch (err: any) { setSnackbar({ open: true, message: err.response?.data?.error || 'Fel', severity: 'error' }); }
    finally { setDynGenerating(false); }
  };

  const handleUpdateDynamic = async () => {
    if (!editingQr || !editUrl.trim()) return;
    try {
      await updateDynamicQr(editingQr.qrId, { destinationUrl: editUrl.trim() });
      setSnackbar({ open: true, message: 'Destination uppdaterad', severity: 'success' });
      setEditingQr(null); loadDynamic();
    } catch (err: any) { setSnackbar({ open: true, message: err.response?.data?.error || 'Fel', severity: 'error' }); }
  };

  const handleDeleteQrAsset = async (assetId: string) => {
    try {
      await deleteQrCode(assetId);
      setSnackbar({ open: true, message: 'QR-kod borttagen', severity: 'success' });
      loadHistory();
    } catch {
      setSnackbar({ open: true, message: 'Kunde inte ta bort', severity: 'error' });
    }
  };

  const handleDeleteDynamic = async (qrId: string) => {
    try {
      await deleteDynamicQr(qrId);
      setSnackbar({ open: true, message: 'Dynamisk QR-kod borttagen', severity: 'success' });
      loadDynamic();
    } catch (err: any) { setSnackbar({ open: true, message: err.response?.data?.error || 'Fel', severity: 'error' }); }
  };

  const update = (partial: Partial<QrOptions>) => setOptions(prev => ({ ...prev, ...partial }));

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const result = await uploadQrLogo(file);
      await loadLogos();
      update({ logoFilename: result.filename });
      setSnackbar({ open: true, message: `Logotyp "${result.originalName}" uppladdad`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kunde inte ladda upp logotyp', severity: 'error' });
    } finally { setLogoUploading(false); }
  };

  const handleDeleteLogo = async (filename: string) => {
    try {
      await deleteQrLogo(filename);
      if (options.logoFilename === filename) update({ logoFilename: undefined });
      await loadLogos();
    } catch {
      setSnackbar({ open: true, message: 'Kunde inte ta bort logotyp', severity: 'error' });
    }
  };

  const logoSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleLogoSearch = (query: string) => {
    setLogoSearch(query);
    if (logoSearchTimeout.current) clearTimeout(logoSearchTimeout.current);
    if (query.trim().length < 2) { setLogoSearchResults([]); return; }
    logoSearchTimeout.current = setTimeout(async () => {
      setLogoSearching(true);
      try { setLogoSearchResults(await searchAssetsForLogo(query.trim())); }
      catch { setLogoSearchResults([]); }
      finally { setLogoSearching(false); }
    }, 300);
  };

  const handleSelectAssetLogo = (asset: LogoSearchResult) => {
    setSelectedAssetLogo(asset);
    update({ logoAssetId: asset._id, logoFilename: undefined });
    setLogoSearch('');
    setLogoSearchResults([]);
  };

  const handleClearLogo = () => {
    setSelectedAssetLogo(null);
    update({ logoAssetId: undefined, logoFilename: undefined });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Preset tabs */}
      <Box sx={{ px: 2, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'center' }}>
        <Tabs
          value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto"
          sx={{
            flex: 1, minHeight: 40,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              textTransform: 'none', minHeight: 34, borderRadius: '8px', mx: 0.3, px: 2,
              fontSize: '0.82rem', fontWeight: 500, transition: 'all 0.2s ease',
              '&.Mui-selected': { bgcolor: '#616161', color: '#fff' },
              '&:not(.Mui-selected)': { color: 'text.secondary', bgcolor: '#f0f0f0', '&:hover': { bgcolor: '#e0e0e0' } },
            },
          }}
        >
          <Tab label="Grund" value="standard" />
          {presets.map(p => (
            <Tab key={p.id} value={p.id} label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{p.name}</span>
                <Tooltip title={p.locked ? 'Låst – klicka för att låsa upp' : 'Olåst – klicka för att låsa'}>
                  {p.locked
                    ? <LockIcon sx={{ fontSize: 13, color: 'warning.light' }} onClick={e => { e.stopPropagation(); handleToggleLock(p.id); }} />
                    : <LockOpenIcon sx={{ fontSize: 13, opacity: 0.5, '&:hover': { opacity: 1 } }} onClick={e => { e.stopPropagation(); handleToggleLock(p.id); }} />}
                </Tooltip>
                {!p.locked && <Tooltip title="Ta bort"><CloseIcon sx={{ fontSize: 13, opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }} onClick={e => { e.stopPropagation(); handleDeletePreset(p.id); }} /></Tooltip>}
              </Box>
            } />
          ))}
        </Tabs>
        <Tooltip title="Spara nuvarande inställningar">
          <IconButton size="small" onClick={() => setSaveDialogOpen(true)} sx={{ ml: 1 }}><SaveIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: '1 1 60%', minWidth: 0 }}>

            {/* Mode toggle */}
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup value={qrMode} exclusive onChange={(_, v) => { if (v) setQrMode(v); }} size="small">
                <ToggleButton value="static">Statisk QR</ToggleButton>
                <ToggleButton value="dynamic">Dynamisk QR</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                {qrMode === 'static' ? 'URL kodas direkt i QR-bilden' : 'URL kan ändras i efterhand utan att trycka om'}
              </Typography>
            </Box>

            {/* === STATIC MODE === */}
            {qrMode === 'static' && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Statisk QR-kod</Typography>
                <TextField fullWidth label="Namn" placeholder="T.ex. Kampanj våren 2026..." value={nameInput} onChange={e => setNameInput(e.target.value)} variant="outlined" size="small" sx={{ mb: 2 }} />
                <TextField fullWidth multiline minRows={2} maxRows={6} label="Innehåll (URL eller text)" placeholder="https://example.com" value={contentInput} onChange={e => setContentInput(e.target.value)} variant="outlined" size="small" helperText={contentInput.length > 0 ? `${contentInput.length} tecken` : 'Skriv in URL, text eller annan data'} sx={{ mb: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, bgcolor: '#d5d5d5', border: '1px solid #bdbdbd', borderRadius: 1, p: 3, '& svg': { maxWidth: 300, height: 'auto' } }}>
                  {previewLoading ? <CircularProgress size={32} /> :
                    previewError ? <Typography color="error" variant="body2">{previewError}</Typography> :
                    svgPreview ? <div dangerouslySetInnerHTML={{ __html: svgPreview }} /> :
                    <Typography color="text.secondary" variant="body2">Skriv in innehåll för att se förhandsgranskning</Typography>}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button variant="contained" size="large" onClick={handleGenerate} disabled={generating || !contentInput.trim() || !nameInput.trim()} startIcon={generating ? <CircularProgress size={20} color="inherit" /> : undefined} sx={{ flex: 1 }}>
                    {generating ? 'Genererar...' : 'Generera och spara'}
                  </Button>
                  {generatedAssetId && (<>
                    <Button variant="outlined" size="large" onClick={() => downloadQrTiff(generatedAssetId)} startIcon={<DownloadIcon />}>TIFF</Button>
                    <Button variant="outlined" size="large" onClick={() => downloadQrPng(generatedAssetId)} startIcon={<DownloadIcon />}>PNG</Button>
                  </>)}
                </Box>
                {generatedAssetId && (
                  <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>QR-kod "{nameInput}" sparad ({options.dpi} DPI, {options.sizeMm}mm).</Alert>
                )}
              </Paper>
            )}

            {/* === DYNAMIC MODE === */}
            {qrMode === 'dynamic' && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Dynamisk QR-kod</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  QR-koden pekar på Athena-servern som sedan vidareskickar till er destinations-URL. Destinationen kan ändras när som helst utan att trycka om QR-koden.
                </Typography>
                <TextField fullWidth label="Namn" placeholder="T.ex. Produkt X kampanj..." value={dynName} onChange={e => setDynName(e.target.value)} variant="outlined" size="small" sx={{ mb: 2 }} />
                <TextField fullWidth label="Destinations-URL (dit besökaren ska hamna)" placeholder="https://fontana.se/kampanj" value={dynUrl} onChange={e => setDynUrl(e.target.value)} variant="outlined" size="small" helperText="Slutdestinationen – kan ändras i efterhand utan att trycka om QR-koden" sx={{ mb: 2 }} />
                <TextField fullWidth label="Athena-server (ändra ej i produktion)" value={dynBaseUrl} onChange={e => setDynBaseUrl(e.target.value)} variant="outlined" size="small" helperText={dynBaseUrl.includes('localhost') ? '⚠️ Localhost fungerar inte vid skanning med telefon – ändra till https://athena.fontana.se' : `QR-koden kodas som: ${dynBaseUrl}/qr/[id] → redirect till destinations-URL`} sx={{ mb: 3 }} error={dynBaseUrl.includes('localhost')} />
                <Button variant="contained" size="large" onClick={handleDynamicGenerate} disabled={dynGenerating || !dynName.trim() || !dynUrl.trim()} startIcon={dynGenerating ? <CircularProgress size={20} color="inherit" /> : <QrCodeIcon />} sx={{ width: '100%' }}>
                  {dynGenerating ? 'Skapar...' : 'Skapa dynamisk QR-kod'}
                </Button>
              </Paper>
            )}

            {/* Dynamic QR list */}
            {dynamicList.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LinkIcon color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Dynamiska QR-koder</Typography>
                </Box>
                <List dense disablePadding>
                  {dynamicList.map(item => (
                    <ListItem key={item._id} sx={{ borderBottom: '1px solid', borderColor: 'divider', flexDirection: 'column', alignItems: 'stretch', py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Redirect: {window.location.origin}/qr/{item.qrId}
                          </Typography>
                          <Typography variant="caption" color="primary">
                            Pekar på: {item.destinationUrl}
                          </Typography>
                        </Box>
                        <Chip label={`${item.scanCount} skanningar`} size="small" variant="outlined" />
                        <Tooltip title="Ändra destination">
                          <IconButton size="small" onClick={() => { setEditingQr(item); setEditUrl(item.destinationUrl); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {item.assetId && (<>
                          <Tooltip title="Ladda ner TIFF"><IconButton size="small" onClick={() => downloadQrTiff(item.assetId!)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Ladda ner PNG"><IconButton size="small" onClick={() => downloadQrPng(item.assetId!)}><DownloadIcon fontSize="small" color="primary" /></IconButton></Tooltip>
                        </>)}
                        <Tooltip title="Ta bort">
                          <IconButton size="small" onClick={() => handleDeleteDynamic(item.qrId)}>
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Static QR history */}
            {history.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <HistoryIcon color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Sparade QR-koder</Typography>
                </Box>
                <List dense disablePadding>
                  {history.map(item => (
                    <ListItem key={item._id} secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Ladda ner TIFF"><IconButton size="small" onClick={() => downloadQrTiff(item._id)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Ladda ner PNG"><IconButton size="small" onClick={() => downloadQrPng(item._id)}><DownloadIcon fontSize="small" color="primary" /></IconButton></Tooltip>
                        <Tooltip title="Ta bort"><IconButton edge="end" size="small" onClick={() => handleDeleteQrAsset(item._id)}><DeleteIcon fontSize="small" color="error" /></IconButton></Tooltip>
                      </Box>
                    } sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                      {item.previewPaths?.thumbnail && (
                        <ListItemAvatar><Avatar variant="rounded" src={`${API_BASE_URL}${item.previewPaths.thumbnail}`} sx={{ width: 48, height: 48, bgcolor: '#fff', border: '1px solid #e0e0e0' }}>QR</Avatar></ListItemAvatar>
                      )}
                      <ListItemText primary={item.title} secondary={`${new Date(item.uploadedAt).toLocaleDateString('sv-SE')} · ${(item.fileSize / 1024).toFixed(0)} KB`} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Settings panel */}
          <Box sx={{ flex: '0 0 300px', minWidth: 260 }}>
            <Paper sx={{ p: 3, position: 'sticky', top: 16 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Inställningar</Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>Storlek:</Typography>
                  <Slider value={options.sizeMm} min={10} max={100} step={1} onChange={(_, v) => update({ sizeMm: v as number })} size="small" sx={{ flex: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 50, textAlign: 'right' }}>{options.sizeMm} mm</Typography>
                </Box>
              </Box>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Felkorrigering</InputLabel>
                <Select value={options.eclevel} label="Felkorrigering" onChange={e => update({ eclevel: e.target.value as QrOptions['eclevel'] })}>
                  {(['L','M','Q','H'] as const).map(lv => <MenuItem key={lv} value={lv}>{EC_LABELS[lv]}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>DPI</InputLabel>
                <Select value={options.dpi} label="DPI" onChange={e => update({ dpi: Number(e.target.value) })}>
                  <MenuItem value={300}>300 DPI</MenuItem>
                  <MenuItem value={600}>600 DPI – Rekommenderad</MenuItem>
                  <MenuItem value={1200}>1200 DPI</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>Quiet zone:</Typography>
                  <Slider value={options.quietZone} min={0} max={10} step={1} onChange={(_, v) => update({ quietZone: v as number })} size="small" sx={{ flex: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 50, textAlign: 'right' }}>{options.quietZone} mod</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">Minst 4 moduler rekommenderas (ISO)</Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />

              {/* Logo section */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                <ImageIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                Logotyp i QR-kod
              </Typography>
              <Box sx={{ mb: 1 }}>
                {/* Show selected logo */}
                {(selectedAssetLogo || options.logoAssetId || options.logoFilename) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    {selectedAssetLogo?.previewPaths?.thumbnail && (
                      <Avatar variant="rounded" src={`${API_BASE_URL}${selectedAssetLogo.previewPaths.thumbnail}`} sx={{ width: 32, height: 32 }} />
                    )}
                    <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>
                      {selectedAssetLogo?.title || options.logoFilename || 'Vald logotyp'}
                    </Typography>
                    <Tooltip title="Ta bort logotyp">
                      <IconButton size="small" onClick={handleClearLogo}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                    </Tooltip>
                  </Box>
                )}

                {/* Search from DAM */}
                <TextField
                  fullWidth size="small" label="Sök logotyp i systemet"
                  placeholder="Sök på namn, filnamn eller tagg..."
                  value={logoSearch} onChange={e => handleLogoSearch(e.target.value)}
                  sx={{ mb: 0.5 }}
                  InputProps={{ endAdornment: logoSearching ? <CircularProgress size={16} /> : undefined }}
                />
                {logoSearchResults.length > 0 && (
                  <Paper variant="outlined" sx={{ maxHeight: 180, overflow: 'auto', mb: 1 }}>
                    <List dense disablePadding>
                      {logoSearchResults.map(a => (
                        <ListItem key={a._id} component="div" onClick={() => handleSelectAssetLogo(a)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, py: 0.5 }}>
                          <ListItemAvatar sx={{ minWidth: 36 }}>
                            <Avatar variant="rounded" src={a.previewPaths?.thumbnail ? `${API_BASE_URL}${a.previewPaths.thumbnail}` : undefined} sx={{ width: 28, height: 28 }}>
                              <ImageIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={a.title || a.fileName} primaryTypographyProps={{ variant: 'caption' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}

                {/* Upload as alternative */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    component="label" size="small" variant="text" startIcon={logoUploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
                    disabled={logoUploading} sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Ladda upp fil
                    <input type="file" hidden accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleLogoUpload} />
                  </Button>
                  {logos.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={options.logoFilename || ''}
                        onChange={e => { update({ logoFilename: e.target.value || undefined, logoAssetId: undefined }); setSelectedAssetLogo(null); }}
                        displayEmpty size="small"
                        sx={{ fontSize: '0.75rem', height: 30 }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Uppladdade</MenuItem>
                        {logos.map(l => <MenuItem key={l.filename} value={l.filename} sx={{ fontSize: '0.75rem' }}>{l.filename}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {/* Size slider when logo is selected */}
                {(options.logoFilename || options.logoAssetId) && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>Storlek:</Typography>
                      <Slider value={options.logoSizePercent} min={5} max={35} step={1} onChange={(_, v) => update({ logoSizePercent: v as number })} size="small" sx={{ flex: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{options.logoSizePercent}%</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Använd felkorrigering H (30%) för bästa resultat med logotyp</Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>Tips</Typography>
                <Typography variant="caption" color="text.secondary">
                  • TIFF: 1-bit bitmap för tryck<br />
                  • PNG: för digital användning<br />
                  • Min. 20×20mm för förpackningar<br />
                  • Dynamisk QR: URL kan ändras i efterhand<br />
                  • Logotyp: byt till H-felkorrigering vid logotyp
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Save preset dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Spara QR-inställningar</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Namn på inställning" value={presetName} onChange={e => setPresetName(e.target.value)} variant="outlined" size="small" sx={{ mt: 1 }} onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSavePreset} disabled={!presetName.trim()}>Spara</Button>
        </DialogActions>
      </Dialog>

      {/* Edit dynamic QR dialog */}
      <Dialog open={!!editingQr} onClose={() => setEditingQr(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Ändra destination för "{editingQr?.name}"</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Redirect-URL: {window.location.origin}/qr/{editingQr?.qrId}
          </Typography>
          <TextField autoFocus fullWidth label="Ny destinations-URL" value={editUrl} onChange={e => setEditUrl(e.target.value)} variant="outlined" size="small" sx={{ mt: 1 }} onKeyDown={e => { if (e.key === 'Enter') handleUpdateDynamic(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingQr(null)}>Avbryt</Button>
          <Button variant="contained" onClick={handleUpdateDynamic} disabled={!editUrl.trim()}>Spara</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default QrGeneratorPage;
